import { useEffect, useState } from "react";

/** 토큰 값을 **화면에서 계산된 그대로** 읽는다. 패턴 페이지가 값을 손으로 베껴 적으면
 *  토큰을 고쳤을 때 그 페이지만 옛날 값을 말하게 된다 — 카탈로그가 거짓말을 하는 순간
 *  아무도 안 믿는다.
 *
 *  테마가 바뀌면 값도 바뀌므로 다시 읽는다. 공통 v3 는 `<html data-theme>` 하나로만 테마를 가르고
 *  「시스템」 선택도 lib/theme.ts 가 그 속성을 써서 따라가므로, 그 속성만 보면 된다. */
export function useTokenValues(names: readonly string[]): Record<string, string> {
  const [epoch, setEpoch] = useState(0);

  useEffect(() => {
    const bump = () => setEpoch((n) => n + 1);
    const mo = new MutationObserver(bump);
    mo.observe(document.documentElement, { attributes: true, attributeFilter: ["data-theme"] });
    return () => mo.disconnect();
  }, []);

  const [values, setValues] = useState<Record<string, string>>({});
  useEffect(() => {
    const cs = getComputedStyle(document.documentElement);

    /* 커스텀 속성의 계산값은 var() 만 풀리고 **calc() 는 안 풀린다.** 공통 v3 는 배수가 없어 지금은
       calc 로 적힌 토큰이 없지만, 생기면 탐침을 하나 세워 길이로 재서 보여준다 — 브라우저가 재 준
       값이라 계산을 우리가 다시 하지 않는다(다시 하면 그 계산이 두 벌이 된다). */
    const probe = document.createElement("div");
    probe.style.cssText = "position:absolute;visibility:hidden;pointer-events:none;height:0";
    document.body.appendChild(probe);

    const next: Record<string, string> = {};
    for (const n of names) {
      const raw = cs.getPropertyValue(n).trim();
      if (raw.includes("calc(")) {
        probe.style.width = raw;
        const px = probe.getBoundingClientRect().width;
        next[n] = px > 0 ? `${Math.round(px * 10) / 10}px` : raw;
      } else {
        next[n] = raw;
      }
    }

    probe.remove();
    setValues(next);
    // names 는 모듈 상수 배열이라 매 렌더 새로 생기지 않는다.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [epoch, names]);

  return values;
}
